import React, { useState } from 'react';
import {
  Alert, Button, Card, Descriptions, Empty, Input, Modal, Radio, Result, Skeleton, Tooltip,
} from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import useDriveSearch from './useDriveSearch';
import { kindOf, parentPath } from './fileKind';
import { formatBytes, formatDuration } from './format';
import { IDriveNode } from '../../../types/models/pathfinder';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  .search-input {
    max-width: 460px;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
  }

  .card {
    cursor: pointer;

    &.card-selected {
      border-color: var(--ancana-primary, #1890ff);
      box-shadow: 0 0 0 1px var(--ancana-primary, #1890ff);
    }

    .ant-card-body {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      padding: 16px;
    }
  }

  .icon {
    font-size: 28px;
    line-height: 1;
    flex: none;
  }

  .meta {
    min-width: 0;
  }

  .name {
    font-weight: 600;
    /* Drive names are arbitrarily long and often have no spaces. Without this
       one long name stretches its grid column and skews the whole row. */
    overflow-wrap: anywhere;
  }

  .path {
    color: var(--ancana-gray);
    font-size: 12px;
    overflow-wrap: anywhere;
  }

  .sub {
    color: var(--ancana-gray);
    font-size: 12px;
    margin-top: 6px;
  }

  .peek {
    display: inline-block;
    margin-top: 6px;
    font-size: 12px;
  }

  .note {
    color: var(--ancana-gray);
    font-size: 12px;
    max-width: 80ch;
  }

  .modal-path {
    font-family: monospace;
    font-size: 12px;
    overflow-wrap: anywhere;
  }
`;

const absolute = (iso?: string | null) =>
  (iso ? dayjs(iso).format('D MMM YYYY, HH:mm') : '—');

/**
 * One result.
 *
 * Every string rendered here is Drive-authored — a name somebody with edit
 * rights on a shared folder chose. It goes in as JSX text children, which React
 * escapes, and nothing here builds an href out of it.
 */
const ResultCard = ({ node, onOpen, isSelected }: {
  node: IDriveNode;
  onOpen: (n: IDriveNode) => void;
  isSelected?: boolean;
}) => {
  const kind = kindOf(node);
  const parent = parentPath(node);

  return (
    <Card
      className={`card${isSelected ? ' card-selected' : ''}`}
      size="small"
      hoverable
      onClick={() => onOpen(node)}
    >
      <span className="icon" style={{ color: kind.color }}>{kind.icon}</span>
      <div className="meta">
        <div className="name">{node.name}</div>
        {parent && <div className="path">{parent}</div>}
        <div className="sub">
          {kind.label}
          {node.type === 'file' && node.size ? ` · ${formatBytes(node.size)}` : ''}
          {node.modified_time ? ` · ${absolute(node.modified_time)}` : ''}
        </div>
        {node.drive_url && (
          /**
           * Look at the file before choosing it.
           *
           * `href` is `drive_url`, which ancana_api builds from the node id —
           * never `name` or `path`, which are Drive-authored text.
           *
           * `stopPropagation` is what makes this usable in a picker: without
           * it, the click bubbles to the card and chooses the very file the
           * operator was trying to check first. As a picker it is also the only
           * way through to Drive at all — clicking a card selects rather than
           * opening the detail modal, so the "Open in Drive" button that modal
           * offers is unreachable, and people were left guessing which
           * `Planta baja.pdf` was the right one from the title alone.
           */
          <Tooltip title="Opens in Drive, in a new tab. Uses your own Drive access.">
            <a
              className="peek"
              href={node.drive_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              <ExportOutlined />
              {' '}
              Preview in Drive
            </a>
          </Tooltip>
        )}
      </div>
    </Card>
  );
};

/**
 * What one node is, and a way out to Drive.
 *
 * Deliberately not a file preview. Rendering the document would mean pulling
 * its bytes through `/v1/fetch`, which is a separate piece of work with its own
 * decisions to make about caching and about who may read which file. Until
 * that exists, the honest move is to hand the file to Drive, which already
 * renders every one of these formats and already knows the viewer's own
 * permissions.
 */
const DetailModal = ({ node, onClose }: { node?: IDriveNode; onClose: () => void }) => {
  if (!node) return null;
  const kind = kindOf(node);

  return (
    <Modal
      visible
      title={node.name}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button
          key="drive"
          type="primary"
          icon={<ExportOutlined />}
          disabled={!node.drive_url}
          href={node.drive_url || undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Drive
        </Button>,
      ]}
    >
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Type">
          <span style={{ color: kind.color, marginRight: 8 }}>{kind.icon}</span>
          {kind.label}
        </Descriptions.Item>
        <Descriptions.Item label="Path">
          <span className="modal-path">{node.path}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Root">{node.root_key}</Descriptions.Item>
        {node.type === 'file' && (
          <Descriptions.Item label="Size">
            {node.size ? formatBytes(node.size) : 'Google-native — no byte size'}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Modified">{absolute(node.modified_time)}</Descriptions.Item>
        <Descriptions.Item label="Mime type">
          <span className="modal-path">{node.mime_type || '—'}</span>
        </Descriptions.Item>
      </Descriptions>
      <p className="note" style={{ marginTop: 12 }}>
        Opening in Drive uses your own Drive access, not Pathfinder&apos;s. If you cannot
        open the file personally, Drive will offer to request access.
      </p>
    </Modal>
  );
};

/**
 * Search the Drive corpus.
 *
 * The match is Pathfinder&apos;s: a substring match over each node&apos;s full path,
 * AND-ed across the words you type. So `tulum aura planta` finds a file whose
 * own name only contains "Planta" — the earlier words are matched against the
 * folders it sits in. Worth saying on the page, because it is the difference
 * between this feeling precise and feeling broken.
 */
export interface DriveSearchProps {
  /**
   * Turns the grid into a picker: clicking a card chooses that file rather
   * than opening the detail modal.
   *
   * Absent, this component behaves exactly as it does on /data-index — which
   * is why it is a prop rather than a separate component. The valuable part
   * here is not the grid, it is the handling of a degraded index, a truncated
   * result set and an unreachable Pathfinder; forking that to build a picker
   * would mean two places to keep those honest.
   */
  onSelect?: (node: IDriveNode) => void;
  /** Which card to mark as chosen. */
  selectedId?: string;
  /**
   * Only these mime types may be picked. Files of other types are hidden
   * rather than shown-and-disabled: the list is long, and a grid of things you
   * cannot click reads as a broken page.
   */
  acceptMimes?: string[];
  /** Runs one search on mount. For seeding a picker with the property name. */
  initialTerm?: string;
  /** Drops the explanatory prose, for use inside a modal that has its own. */
  compact?: boolean;
}

const DriveSearch = ({
  onSelect, selectedId, acceptMimes, initialTerm, compact,
}: DriveSearchProps = {}) => {
  const { result, isLoading, error, isForbidden, term, search } = useDriveSearch();
  const [draft, setDraft] = useState(initialTerm || '');
  // A picker only ever wants files, so the type control is both preset and
  // hidden: offering "Folders" in a file picker is an invitation to a dead end.
  const [type, setType] = useState(onSelect ? 'file' : '');
  const [selected, setSelected] = useState<IDriveNode>();

  const run = (value: string, nextType = type) => search(value, { type: nextType || undefined });

  // Seed the picker once. `search` is stable (useCallback with no deps) and
  // the guard is on the term, so this cannot re-fire and stamp on whatever the
  // user has typed since.
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (initialTerm && !seeded.current) {
      seeded.current = true;
      run(initialTerm);
    }
  }, [initialTerm]);

  const visible = React.useMemo(() => {
    const rows = result?.results || [];
    if (!acceptMimes) return rows;
    return rows.filter(node => node.mime_type && acceptMimes.includes(node.mime_type));
  }, [result, acceptMimes]);

  const hidden = (result?.results.length || 0) - visible.length;

  if (isForbidden) {
    return (
      <Result
        status="403"
        title="Platform staff only"
        subTitle="Searching the Drive index needs an active platform membership on the platform-owner operator."
      />
    );
  }

  return (
    <Container>
      <div className="controls">
        <Input.Search
          className="search-input"
          placeholder="e.g. tulum aura planta"
          allowClear
          enterButton="Search"
          size="large"
          value={draft}
          loading={isLoading}
          onChange={e => setDraft(e.target.value)}
          onSearch={value => run(value)}
        />
        {!onSelect && (
        <Radio.Group
          value={type}
          onChange={e => {
            setType(e.target.value);
            // Re-run against the term that produced what is on screen, not the
            // draft: changing a filter should refine these results, not
            // silently search for something half-typed.
            if (term) run(term, e.target.value);
          }}
        >
          <Radio.Button value="">Everything</Radio.Button>
          <Radio.Button value="file">Files</Radio.Button>
          <Radio.Button value="folder">Folders</Radio.Button>
        </Radio.Group>
        )}
      </div>

      {!compact && (
      <p className="note">
        Matches every word you type against the file&apos;s full path, so
        <strong> tulum aura planta </strong>
        finds a plan inside the Tulum/Aura folders. Searching by property or document type
        is not available yet — those filters read the nomenclature parser, which still ships
        with no rules.
      </p>
      )}

      {error && <Alert type="error" showIcon message="The search could not be run" description={error} />}

      {result?.degraded && (
        <Alert
          type="warning"
          showIcon
          message="These results come from a degraded index"
          description={
            `The last sync of at least one root did not complete, so a file added or renamed `
            + `recently may be missing. Index age: ${formatDuration(result.stale_seconds)}. `
            + `See the Index health tab.`
          }
        />
      )}

      {result?.truncated && (
        <Alert
          type="info"
          showIcon
          message="More files matched than are shown"
          description="Only the first page of matches is listed. Add another word to narrow it down."
        />
      )}

      {isLoading && !result && <Skeleton active paragraph={{ rows: 4 }} />}

      {result && !visible.length && (
        <Empty description={
          hidden > 0
            ? `Nothing matching "${term}" is a PDF or an image`
            : `Nothing in the index matches "${term}"`
        }
        />
      )}

      {result && visible.length > 0 && (
        <>
          <span className="note">
            {visible.length}
            {result.truncated ? '+' : ''}
            {' '}
            {visible.length === 1 ? 'match' : 'matches'}
            {hidden > 0 ? ` · ${hidden} hidden (not a PDF or image)` : ''}
            {' · '}
            <Tooltip title={`Pathfinder last synced ${absolute(result.last_sync)}`}>
              <span>{`index ${formatDuration(result.stale_seconds)} old`}</span>
            </Tooltip>
          </span>
          <div className="grid">
            {visible.map(node => (
              <ResultCard
                key={node.id}
                node={node}
                isSelected={node.id === selectedId}
                onOpen={onSelect || setSelected}
              />
            ))}
          </div>
        </>
      )}

      {!result && !isLoading && !error && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Search the propiedades and sales folders by name or path."
        />
      )}

      <DetailModal node={selected} onClose={() => setSelected(undefined)} />
    </Container>
  );
};

export default DriveSearch;
